import React, { useState } from 'react';
import './Footer.css';
import {
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaTwitter,
  FaInstagram, FaWhatsapp, FaComments, FaTimes, FaPaperPlane, FaYoutube,
  FaUsers, FaGraduationCap, FaHandsHelping, FaUserShield, FaChevronDown, FaChevronUp,
  FaArrowUp
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext';

const Footer = () => {
  const { t } = useTranslation();
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: t('chatWelcome'), isBot: true, time: t('justNow') }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [showQuestions, setShowQuestions] = useState(false);

  // ✅ Simple subscribe state — no backend, no storage
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const suggestedQuestions = [
    t('chatQuestionHire'),
    t('chatQuestionDonate'),
    t('chatQuestionContact')
  ];

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    const newUserMessage = {
      id: messages.length + 1,
      text: inputMessage,
      isBot: false,
      time: t('justNow')
    };

    setMessages([...messages, newUserMessage]);
    setShowQuestions(false);

    setTimeout(() => {
      const responses = {
        [t('chatKeywordHire').toLowerCase()]: t('chatResponseHire'),
        [t('chatKeywordEmployer').toLowerCase()]: t('chatResponseEmployer'),
        [t('chatKeywordRequest').toLowerCase()]: t('chatResponseRequest'),
        [t('chatKeywordDonate').toLowerCase()]: t('chatResponseDonate'),
        [t('chatKeywordSupport').toLowerCase()]: t('chatResponseSupport'),
        [t('chatKeywordTraining').toLowerCase()]: t('chatResponseTraining'),
        [t('chatKeywordApply').toLowerCase()]: t('chatResponseApply'),
        [t('chatKeywordLogin').toLowerCase()]: t('chatResponseLogin'),
        [t('chatKeywordRegister').toLowerCase()]: t('chatResponseRegister'),
        [t('chatKeywordAbout').toLowerCase()]: t('chatResponseAbout'),
        [t('chatKeywordGallery').toLowerCase()]: t('chatResponseGallery'),
        [t('chatKeywordDepartments').toLowerCase()]: t('chatResponseDepartments'),
        [t('chatKeywordContact').toLowerCase()]: t('chatResponseContact'),
        [t('chatKeywordLocation').toLowerCase()]: t('chatResponseLocation'),
        "default": t('chatResponseDefault')
      };

      const lowerCaseMessage = inputMessage.toLowerCase();
      let response = responses.default;

      Object.keys(responses).forEach(key => {
        if (lowerCaseMessage.includes(key)) {
          response = responses[key];
        }
      });

      const botResponse = {
        id: messages.length + 2,
        text: response,
        isBot: true,
        time: t('justNow')
      };

      setMessages(prev => [...prev, botResponse]);
    }, 1000);

    setInputMessage('');
  };

  const handleQuestionClick = (question) => {
    setInputMessage(question);
    document.querySelector('.chat-input input')?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = "+250788350577";
    const message = t('whatsappDefaultMessage');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============================================================
  // ✅ SIMPLE SUBSCRIBE HANDLER
  // Validates the email, then opens YouTube subscribe dialog.
  // Nothing is stored, nothing is sent to a server.
  // ============================================================
  const handleSubscribe = (e) => {
    e.preventDefault();

    const email = subscriberEmail.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!isValidEmail) {
      setSubscribeMessage(
        t('subscribeEmailInvalid') || 'Please enter a valid email address.'
      );
      setTimeout(() => setSubscribeMessage(''), 4000);
      return;
    }

    // Clear the input + show brief message
    setSubscriberEmail('');
    setSubscribeMessage(
      t('subscribeRedirect') || 'Opening YouTube so you can subscribe…'
    );
    setTimeout(() => setSubscribeMessage(''), 4000);

    // Open YouTube subscribe dialog in a new tab
    window.open(
      'https://www.youtube.com/@urugowoc3033?sub_confirmation=1',
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <>
      {/* Fixed Left Buttons: Back to Top + WhatsApp */}
      <div className="fixed-buttons-left">
        <button className="back-to-top" onClick={scrollToTop} title={t('backToTop')}>
          <FaArrowUp />
        </button>
        <button className="whatsapp-button" onClick={handleWhatsAppClick} title={t('whatsappTooltip')}>
          <FaWhatsapp />
          <span className="whatsapp-tooltip">{t('whatsappTooltip')}</span>
        </button>
      </div>

      {/* Chat Bot */}
      <div className={`chat-bot ${chatOpen ? 'open' : ''}`}>
        <div className="chat-header">
          <div className="chat-title">
            <FaComments />
            <h3>{t('chatTitle')}</h3>
          </div>
          <button className="close-chat" onClick={() => setChatOpen(false)} title={t('close')}>
            <FaTimes />
          </button>
        </div>

        <div className="chat-messages">
          {messages.map(message => (
            <div
              key={message.id}
              className={`message ${message.isBot ? 'bot-message' : 'user-message'}`}
            >
              <div className="message-content">
                <p>{message.text}</p>
                <span className="message-time">{message.time}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="suggested-questions">
          <button
            className="toggle-questions-btn"
            onClick={() => setShowQuestions(!showQuestions)}
            aria-expanded={showQuestions}
          >
            <span>{t('chatQuickQuestions')}</span>
            {showQuestions ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {showQuestions && (
            <div className="question-buttons">
              {suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  className="question-btn"
                  onClick={() => handleQuestionClick(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="chat-input">
          <input
            type="text"
            placeholder={t('chatPlaceholder')}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button className="send-btn" onClick={handleSendMessage} title={t('send')}>
            <FaPaperPlane />
          </button>
        </div>
      </div>

      {/* Chat Toggle Button (Right Side) */}
      <button className="chat-toggle" onClick={() => setChatOpen(!chatOpen)} title={t('chatToggleTooltip')}>
        <FaComments />
        {!chatOpen && <span className="chat-notification">1</span>}
      </button>

      {/* Footer content */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>{t('footerTitle')}</h3>
              <p>{t('footerDescription')}</p>
              <div className="social-links">
                <a href="https://web.facebook.com/search/top/?q=urugo%20women%27s%20opportunity%20center" title={t('facebook')}><FaFacebook /></a>
                <a href="https://x.com/UrugoWoc" title={t('twitter')}><FaTwitter /></a>
                <a href="https://www.instagram.com/urugo_woc?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" title={t('instagram')}><FaInstagram /></a>
                <a href="https://www.youtube.com/@urugowoc3033" title={t('youtube')}><FaYoutube /></a>
                <a href="#" onClick={handleWhatsAppClick} title={t('whatsapp')}><FaWhatsapp /></a>
              </div>
            </div>

            <div className="footer-section">
              <h4>{t('quickLinks')}</h4>
              <ul>
                <li><a href="/">{t('home')}</a></li>
                <li><a href="/about">{t('about')}</a></li>
                <li><a href="#departments">{t('departments')}</a></li>
                <li><a href="#women">{t('women')}</a></li>
                <li><a href="/gallery">{t('gallery')}</a></li>
                <li><a href="#departments">{t('hire')}</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>{t('contactInfo')}</h4>
              <div className="contact-infoo">
                <p><FaMapMarkerAlt /> 3FHW+W33, Kayonza, Rwanda</p>
                <p><FaPhone /> {t('phoneNumber')}</p>
                <p><FaEnvelope /> info@urugowoc.com</p>
              </div>

              <div className="footer-actions">
                <a href="#departments" className="footer-action-btn">
                  <FaUsers /> {t('hireWomen')}
                </a>
              </div>

              {/* ✅ SUBSCRIBE / YOUTUBE SECTION */}
              <div className="footer-subscribe">
                <div className="footer-subscribe-heading">
                  <FaYoutube className="footer-subscribe-icon" />
                  <span>{t('subscribeYoutube') || 'Subscribe to our YouTube channel'}</span>
                </div>

                <form className="footer-subscribe-form" onSubmit={handleSubscribe} noValidate>
                  <input
                    type="email"
                    className="footer-subscribe-input"
                    placeholder={t('subscribeEmailPlaceholder') || 'Enter your email'}
                    value={subscriberEmail}
                    onChange={(e) => {
                      setSubscriberEmail(e.target.value);
                      if (subscribeMessage) setSubscribeMessage('');
                    }}
                    aria-label={t('Enter your email') || 'Enter your email'}
                  />
                  <button type="submit" className="footer-subscribe-btn">
                    <FaYoutube />
                    <span>{t('subscribe') || 'Subscribe'}</span>
                  </button>
                </form>

                {subscribeMessage && (
                  <p className="footer-subscribe-message" role="status">
                    {subscribeMessage}
                  </p>
                )}
              </div>

              
            </div>

            <div className="footer-section">
              <h4>{t('ourDepartments')}</h4>
              <ul>
                <li><FaGraduationCap /> {t('coffeeShopOperation')}</li>
                <li><FaGraduationCap /> {t('housekeepingOperation')}</li>
                <li><FaGraduationCap /> {t('receptionOperation')}</li>
                <li><FaGraduationCap /> {t('sewingOperation')}</li>
                <li><FaGraduationCap /> {t('leatherOperation')}</li>
                <li><FaGraduationCap /> {t('carpetOperation')}</li>
                <li><FaGraduationCap /> {t('milkYogurtProduction')}</li>
                <li><FaGraduationCap />Weaving</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} {t('copyright')}</p>
            <div className="footer-links simple-footer">
              <div className="footer-nav">
                <a href="/privacy" className="nav-link">
                  <div className="nav-item">
                    <span className="nav-icon">🔒</span>
                    <span className="nav-text">{t('privacyPolicy')}</span>
                  </div>
                </a>
                <div className="nav-divider"></div>
                <a href="/terms" className="nav-link">
                  <div className="nav-item">
                    <span className="nav-icon">📄</span>
                    <span className="nav-text">{t('termsOfService')}</span>
                  </div>
                </a>
                <div className="nav-divider"></div>
                <a href="/sitemap" className="nav-link">
                  <div className="nav-item">
                    <span className="nav-icon">🗺️</span>
                    <span className="nav-text">{t('sitemap')}</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;